// ============================================================
// FeuilleAtelier — le formulaire de l'ATELIER, ouvert par le QR.
// ------------------------------------------------------------
// Il n'a rien du reste de l'application, et c'est délibéré. On le
// remplit debout, devant une machine, sur un téléphone, avec des mains
// qui ne sont pas propres. Tout ce qui n'aide pas à ça a été enlevé :
// pas de barre latérale, pas de menu, pas de connexion.
//
// ── UNE CASE PAR OPÉRATION ──
//
// Pas trois totaux. Trois totaux obligeraient l'opérateur à faire la
// somme de ses prises avant de l'écrire : on lui demanderait un calcul
// mental pour éviter au programme une addition, et une somme faite de
// tête à la machine est une erreur que personne ne verra jamais.
//
// Les totaux s'affichent, calculés en direct — pour qu'il VOIE la somme
// sans avoir à la faire.
//
// ── L'AUTOSAVE, à chaque valeur ──
//
// Il n'y a pas de bouton « enregistrer ». Deux raisons, et la seconde
// est la vraie :
//
//  1. un bouton s'oublie, et un téléphone qui se verrouille au milieu
//     d'une saisie perdrait tout ;
//  2. **plusieurs opérateurs travaillent sur la même pièce.** L'un fait
//     la prise 1 le matin, l'autre la prise 2 l'après-midi. Le second
//     doit voir ce que le premier a écrit et compléter — ce qui n'est
//     possible que si chaque valeur est déjà en base.
//
// Chaque champ dit où il en est : « enregistré », « en cours »,
// « échec ». Un enregistrement silencieux ne se vérifie pas, et sur ce
// téléphone-là personne ne rouvrira la page pour contrôler.
//
// ── Ce que cette page ne montre pas ──
//
// Aucun prix, aucun client, aucune coordonnée. Le service ne les
// sélectionne même pas : ce qu'il ne renvoie pas ne peut pas fuir par
// ce chemin.

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../lib/api.js';
import { totauxAffichables } from '@shared/gamme.js';
import { formatMesure } from '../../lib/format.js';
import ChampAuto from './ChampAuto.jsx';
import TypeTemps from './TypeTemps.jsx';
import './atelier.css';

export function FeuilleAtelier() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    try {
      setData(await api.get(`/atelier/feuille/${token}`));
    } catch (err) {
      setErreur(err);
    }
  }, [token]);

  useEffect(() => {
    charger();
  }, [charger]);

  const enregistrer = useCallback(
    async (gammeId, champ, valeur) => {
      await api.post(`/atelier/feuille/${token}/valeur`, { gamme_id: gammeId, champ, valeur });
      // On RELIT après écriture : les totaux se recalculent côté serveur
      // par la même fonction que les temps prévus, et c'est elle qui
      // fait foi — pas une addition refaite dans le navigateur.
      await charger();
    },
    [token, charger],
  );

  if (erreur) {
    return (
      <main className="atelier">
        <h1 className="atelier__titre">Feuille introuvable</h1>
        <p className="atelier__note">
          Ce code ne correspond à aucune pièce. Vérifiez le numéro imprimé sur la feuille, ou
          prévenez le bureau.
        </p>
      </main>
    );
  }
  if (!data) return <main className="atelier"><p className="atelier__note">Chargement…</p></main>;

  const fige = data.entete.status !== 'draft';
  // Combien d'opérations portent déjà un temps réel. Zéro veut dire
  // « rien de saisi », pas « zéro minute ».
  const saisi = data.gamme.filter((op) => op.temps_reel_min != null).length;

  return (
    <main className="atelier">
      {/* L'identité, en tête et en gros : sur un téléphone, on doit
          savoir de quelle pièce on parle sans faire défiler. */}
      <header className="atelier__tete">
        <p className="atelier__ref">{data.piece.ref_interne}</p>
        <h1 className="atelier__titre">{data.piece.name || data.piece.ref || 'Pièce'}</h1>
        <p className="atelier__note">
          {data.piece.ref ? `Réf. ${data.piece.ref} · ` : ''}
          {data.piece.qty} pièce(s) · {data.piece.material_label || 'matière non précisée'}
        </p>
      </header>

      {fige ? (
        <p className="atelier__fige" role="status">
          Ce retour a déjà été traité au bureau. Il ne se modifie plus ici.
        </p>
      ) : null}

      <section>
        <h2 className="atelier__section">Temps passé, opération par opération</h2>
        {data.gamme.length === 0 ? (
          <p className="atelier__note">
            Cette pièce n’a pas de gamme. Notez le temps total dans « quantité produite » et
            prévenez le bureau.
          </p>
        ) : null}

        {data.gamme.map((op) => (
          <div className="atelier__op" key={op.id}>
            {/* La MACHINE n'est pas affichée, et c'est une décision :
                celui qui remplit est devant elle. La lui nommer occupe
                une ligne pour rien sur un écran de téléphone — et si le
                bureau avait prévu une autre machine que celle qui a
                réellement tourné, l'écrire ici suggérerait de corriger
                le devis au lieu de noter le temps. */}
            <div className="atelier__op-tete">
              <span className="atelier__op-num">OP {op.op_num}</span>
              {op.prise_num ? <span className="atelier__op-prise">prise {op.prise_num}</span> : null}
            </div>
            <p className="atelier__op-libelle">{op.libelle}</p>
            <TypeTemps categorie={op.categorie} unite={op.unite} />
            <p className="atelier__note">
              prévu <strong>{formatMesure(op.temps_min, 'min')}</strong>
            </p>
            {fige ? (
              <p className="atelier__note">
                Réel : <strong>{formatMesure(op.temps_reel_min, 'min')}</strong>
              </p>
            ) : (
              <ChampAuto
                label="Temps réel"
                valeur={op.temps_reel_min}
                unite="min"
                onEnregistrer={(v) => enregistrer(op.id, 'temps_reel_min', v)}
              />
            )}
          </div>
        ))}
        {/* La même phrase que la feuille imprimée : la règle est celle de
            `lignesDeLaFeuille`, partagée par les deux supports. */}
        {data.lignes_masquees ? (
          <p className="atelier__note">
            Une seule pièce à faire : il n’y a pas de production. La mise en route la produit.
            Les {data.lignes_masquees} ligne(s) de production et de contrôle de série restent dans
            le devis.
          </p>
        ) : null}
      </section>

      {/* ── LES TOTAUX, CALCULÉS ──
          Il ne les saisit pas : il les VOIT. C'est tout l'objet d'une
          case par opération — l'addition est le travail du programme.

          Tant que rien n'est saisi, on n'affiche PAS « 0 min » : la
          dérivation rend honnêtement zéro sur une liste vide, mais
          « Cycle 0 min par pièce » se lit « cette pièce n'a pris aucun
          temps ». Un zéro converti reste un zéro converti, même quand
          c'est une somme qui le produit. */}
      <section className="atelier__totaux">
        <h2 className="atelier__section">Ce que ça fait au total</h2>
        {saisi === 0 ? (
          <p className="atelier__note">
            Rien de saisi pour l’instant. Les totaux s’afficheront ici, vous n’avez aucune
            addition à faire.
          </p>
        ) : (
          <>
            {/* Les totaux viennent d'UNE fonction partagée : la feuille,
                le retour d'atelier et l'écran de chiffrage affichent les
                mêmes, quel que soit le modèle de la gamme. Refaire le
                test ici ferait afficher « 0 min » le jour où le modèle
                change et qu'on oublie un écran. */}
            {totauxAffichables(data.reel).map((t) => (
              <p key={t.cle}>
                {t.label} <strong>{formatMesure(t.minutes, 'min')}</strong> {t.portee}
              </p>
            ))}
            {saisi < data.gamme.length ? (
              <p className="atelier__note">
                {data.gamme.length - saisi} opération(s) pas encore renseignée(s) : ces totaux
                ne sont pas complets.
              </p>
            ) : null}
          </>
        )}
      </section>

      <section className="atelier__carte">
        <h2 className="atelier__section">Qui, combien</h2>
        {fige ? (
          <p className="atelier__note">
            {data.entete.operator || '—'} · {data.entete.produced_qty ?? '—'} bonne(s) ·{' '}
            {data.entete.scrap_qty ?? '—'} rebut(s)
          </p>
        ) : (
          <>
            <ChampAuto
              label="Votre nom"
              type="text"
              valeur={data.entete.operator}
              onEnregistrer={(v) => enregistrer(null, 'operator', v)}
            />
            <ChampAuto
              label="Pièces bonnes"
              valeur={data.entete.produced_qty}
              onEnregistrer={(v) => enregistrer(null, 'produced_qty', v)}
            />
            <ChampAuto
              label="Rebuts"
              valeur={data.entete.scrap_qty}
              onEnregistrer={(v) => enregistrer(null, 'scrap_qty', v)}
            />
          </>
        )}
      </section>

      <p className="atelier__note atelier__note--fin">
        Chaque valeur est enregistrée dès que vous quittez la case — il n’y a rien à valider.
        Un collègue peut compléter la feuille après vous. Le bureau vérifie avant que ça compte.
      </p>
    </main>
  );
}

export default FeuilleAtelier;
